// ** React Imports
import { Link } from "react-router-dom"
import { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'

// ** Custom Components
import Avatar from "@components/avatar"

// ** Utils
import { isUserLoggedIn } from "@utils"
import { getMainRole } from "@src/utility/roleUtils"

// ** Store & Actions
import { useDispatch, useSelector } from "react-redux"
import { handleLogout } from "@store/authentication"
import { logout as logoutAuthSlice, clearCredentials, selectUserRoles } from "@store/slices/authSlice"

// ** Third Party Components
import {
  User,
  Mail,
  CheckSquare,
  MessageSquare,
  Settings,
  CreditCard,
  HelpCircle,
  Power
} from "react-feather"

// ** Reactstrap Imports
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem
} from "reactstrap"

const UserDropdown = () => {
  const { t } = useTranslation()
  
  // ** Store Vars
  const dispatch = useDispatch()
  const userRoles = useSelector(selectUserRoles) || []

  // ** State
  const [userData, setUserData] = useState(null)

  //** ComponentDidMount
  useEffect(() => {
    const loadUserData = () => {
      if (isUserLoggedIn() !== null) {
        setUserData(JSON.parse(localStorage.getItem("userData")))
      }
    }
    
    // Carregar dados iniciais
    loadUserData()
    
    // Listener para mudanças no localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'userData') {
        loadUserData()
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  //** Vars
  const userAvatar = (userData && userData.dataGeneral?.avatar)
  const hasAvatar = userAvatar !== null
  
  // Obter a maior permissão do array de roles
  // Prioridade: Redux userRoles > userData.roles (array) > userData.role (string) > []
  let rolesArray = []
  if (userRoles.length > 0) {
    rolesArray = userRoles
  } else if (userData?.roles && Array.isArray(userData.roles)) {
    rolesArray = userData.roles
  } else if (userData?.role) {
    // Se role é uma string única, converter para array
    rolesArray = Array.isArray(userData.role) ? userData.role : [userData.role]
  }
  
  const mainRole = getMainRole(rolesArray)
  const roleDisplayName = mainRole ? t(`role.${mainRole}`) : (userData?.dataGeneral?.displayName || userData?.role || t("Invalid"))

  return (
    <UncontrolledDropdown tag="li" className="dropdown-user nav-item">
      <DropdownToggle
        href="/"
        tag="a"
        className="nav-link dropdown-user-link"
        onClick={(e) => e.preventDefault()}
      >
        <div className="user-nav d-sm-flex d-none">
          <span className="user-name fw-bold">
            {(userData && (
              userData.dataGeneral?.nickname || 
              (userData.dataGeneral?.fname && userData.dataGeneral?.lname ? 
                `${userData.dataGeneral.fname} ${userData.dataGeneral.lname}` : 
                userData.dataGeneral?.fname || userData.dataGeneral?.lname
              )
            )) || t("Unknown")}
          </span>
          <span className="user-status">
            {roleDisplayName}
          </span>
        </div>
        <Avatar 
          img={hasAvatar && userAvatar} 
          icon={!hasAvatar && <User size={20} />}
          imgHeight="40" 
          imgWidth="40" 
          status="online" 
        />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem tag={Link} to="/profile">
          <User size={14} className="me-75" />
          <span className="align-middle">{t("Profile")}</span>
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          tag={Link}
          to="/login"
          onClick={async () => {
            // Limpar estado do Redux primeiro
            dispatch(clearCredentials())
            // Limpar localStorage e estado do sistema antigo
            dispatch(handleLogout())
            // Chamar API de logout (já limpa localStorage novamente como segurança)
            await dispatch(logoutAuthSlice())
          }}
        >
          <Power size={14} className="me-75" />
          <span className="align-middle">{t("Logout")}</span>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

export default UserDropdown
